import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { type ApiError } from "../api/http";
import type {
  Business,
  CreateBusinessRequest,
  FeatureFlagKey,
  UpdateBusinessRequest,
} from "../api/types";

export const businessKeys = {
  my: ["business", "me"] as const,
};

export function useMyBusiness() {
  return useQuery<Business, ApiError>({
    queryKey: businessKeys.my,
    queryFn: () => api.get<Business>("/api/v1/businesses/me/"),
    retry: (failureCount, error) => {
      // Don't retry on 403 (no business yet)
      if (error.status === 403) return false;
      return failureCount < 1;
    },
    // A brand-new user has no business, so this query legitimately errors (403).
    // Many components observe it (AppLayout, HomePage, BusinessSetupPage, …).
    // With React Query's default retryOnMount=true, each newly-mounted observer
    // refetches the errored query, flipping its status pending→error→pending. On
    // the onboarding screen HomePage shows the spinner while pending and mounts
    // BusinessSetupPage while errored — and BusinessSetupPage itself observes this
    // query, so its mount re-arms the refetch: an infinite /businesses/me/ loop
    // that live-locks the app. Don't refetch an errored query on mount.
    retryOnMount: false,
  });
}

export function useCreateBusiness() {
  const queryClient = useQueryClient();
  return useMutation<Business, ApiError, CreateBusinessRequest>({
    mutationFn: (data) => api.post<Business>("/api/v1/businesses/", data),
    onSuccess: (business) => {
      queryClient.setQueryData(businessKeys.my, business);
      // Belt-and-suspenders: refetch the canonical object in the background so
      // any drift between the POST response and GET /businesses/me/ (e.g. a
      // context-less serialization of is_viewer_admin/section_permissions)
      // self-heals immediately instead of after the 5-minute staleTime.
      queryClient.invalidateQueries({ queryKey: businessKeys.my });
    },
  });
}

export function useUpdateBusiness() {
  const queryClient = useQueryClient();
  return useMutation<Business, ApiError, UpdateBusinessRequest>({
    mutationFn: (data) => api.patch<Business>("/api/v1/businesses/me/", data),
    onSuccess: (business) => {
      queryClient.setQueryData(businessKeys.my, business);
    },
  });
}

export function useUpdateBusinessFeatures() {
  const queryClient = useQueryClient();
  return useMutation<Business, ApiError, Record<FeatureFlagKey, boolean>>({
    mutationFn: (features) =>
      api.patch<Business>("/api/v1/businesses/me/", { features }),
    onSuccess: (business) => {
      queryClient.setQueryData(businessKeys.my, business);
    },
  });
}

export function useUploadBusinessLogo() {
  const queryClient = useQueryClient();
  return useMutation<Business, ApiError, FormData>({
    mutationFn: (formData) => api.upload<Business>("/api/v1/businesses/me/", formData),
    onSuccess: (business) => {
      queryClient.setQueryData(businessKeys.my, business);
    },
  });
}
